import React, { useState, useEffect } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import { Header, Table, Icon } from 'semantic-ui-react';
import { Button } from '~/components';
import { useFadeInStyles } from '~/hooks';
import { usePageType } from '~/hooks';
import { useProjectContext, usePortfolioContext } from '~/hooks';
import { Portfolio } from '~/types/portfolio';
import { Project, ProjectInvestorQueue } from '~/types/project';
import InvestorQueueActions from './Actions';
import AddEditInvestorModal from './AddEditInvestorModal';
import investorQueueDisplayValues from '~/utils/displayValues/investorQueue';
import { InvestorBidState, InvestorBidStatus } from '~/constants';
import BidDeadlineForm from '../BidDeadlineForm';
import PreferredInvestors from '../PreferredInvestors';


import './styles.scss';

const InvestorQueueTable = () => {
  const pageType = usePageType();
  const { project } = useProjectContext();
  const { portfolio } = usePortfolioContext();

  const [isQueueOpen, setIsQueueOpen] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const iconName = isQueueOpen ? 'caret down' : 'caret right';
  const parentProject = portfolio?.projects?.find((project: Project) => !project.parentId);
  const investorQueue = pageType === 'project' ? project?.investorQueue : parentProject?.investorQueue;
  const [queueElements, setQueueElements] = useState<React.ReactNode[]>([]);

  useEffect(() => {
    if (investorQueue) {
      buildQueueElements();
    }
  }, [investorQueue]);

  const findProjectStatus = (project: Project, investorId: string) => {
    const bid = project?.bidSummary?.find((bid: any) => bid?.investor?.id === investorId);
    return bid?.projectStatus;
  };

  const findPortfolioStatus = (portfolio: Portfolio, investorId: string, status: string) => {
    const currentInvestorData = portfolio?.projectInvestorData?.filter((row: any) => {
      return row?.investor?.id === investorId;
    });
    if (currentInvestorData && currentInvestorData?.length > 0) {
      const hasStatusAll = currentInvestorData?.every((row: any) => row?.status === status);
      return hasStatusAll;
    } else {
      return false;
    }
  };

  const buildQueueElements = () => {
    const elements = investorQueue?.map((investorRow: ProjectInvestorQueue) => {
      const investorId = investorRow.investor?.id;
      const projectStatus = findProjectStatus(project, investorId);
      const hasPortfolioConversationEnded = findPortfolioStatus(portfolio, investorId, InvestorBidState.BID_REJECTED);
      const hasPortfolioInvestorDeclined = findPortfolioStatus(portfolio, investorId, InvestorBidState.DECLINED_TO_BID);

      const displayValues = investorQueueDisplayValues(investorRow, projectStatus, hasPortfolioInvestorDeclined, hasPortfolioConversationEnded);
      const { name, buyerFee, buyerStorageFee, sellerFee, sellerStorageFee, formattedStatus, actionStatus } = displayValues;

      return (
        <Table.Row
          key={name}
          className="investor-queue-row"
          positive={actionStatus === InvestorBidStatus.ACCEPTED}
          negative={actionStatus === InvestorBidStatus.DECLINED || hasPortfolioConversationEnded || hasPortfolioInvestorDeclined}
        >
          <Table.Cell>{name}</Table.Cell>
          <Table.Cell textAlign='center'>{buyerFee}</Table.Cell>
          <Table.Cell textAlign='center'>{buyerStorageFee}</Table.Cell>
          <Table.Cell textAlign='center'>{sellerFee}</Table.Cell>
          <Table.Cell textAlign='center'>{sellerStorageFee}</Table.Cell>
          <Table.Cell status={actionStatus}>{formattedStatus}</Table.Cell>
          <Table.Cell textAlign='right'>
            <InvestorQueueActions
              investorRow={investorRow}
              invitationStatus={actionStatus}
            />
          </Table.Cell>
        </Table.Row>
      );
    });
    setQueueElements(elements);
  };

  return (
    <div className="investor-queue-container" style={useFadeInStyles()}>
      <Collapsible.Root open={isQueueOpen} onOpenChange={() => setIsQueueOpen(!isQueueOpen)}>
        <Collapsible.Trigger className="investor-queue-header">
          <Icon name={iconName} />
          <Header as='h2' style={{ margin: '0' }}>Investor Queue</Header>
        </Collapsible.Trigger>
        <Collapsible.Content className="CollapsibleContent investor-queue-content">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
            <div style={{ display: 'flex', justifyContent: 'start', alignItems: 'end' }}>
              <BidDeadlineForm />
              <PreferredInvestors type="EXPANDED" />
            </div>
          </div>

          {
            queueElements.length === 0 ?
              <p style={{ padding: '24px 0 0' }}>The investor queue is currently empty.</p>
              :
              <Table basic compact className="investor-queue-table">
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell rowspan='2' color='grey' verticalAlign='bottom'>Investor</Table.HeaderCell>
                    <Table.HeaderCell colSpan='2' color='grey' textAlign='center' style={{ padding: '0.2rem 0.7rem' }}>Buyer fees</Table.HeaderCell>
                    <Table.HeaderCell colSpan='2' color='grey' textAlign='center' style={{ padding: '0.2rem 0.7rem' }}>Seller fees</Table.HeaderCell>
                    <Table.HeaderCell rowspan='2' color='grey' verticalAlign='bottom'>Status</Table.HeaderCell>
                    <Table.HeaderCell width={1} rowspan='2' color='grey' verticalAlign='bottom' textAlign='center'>Actions</Table.HeaderCell>
                  </Table.Row>
                  <Table.Row>
                    <Table.HeaderCell color='grey' textAlign='center' width={2}>PV</Table.HeaderCell>
                    <Table.HeaderCell color='grey' textAlign='center' width={2}>Storage</Table.HeaderCell>
                    <Table.HeaderCell color='grey' textAlign='center' width={2}>PV</Table.HeaderCell>
                    <Table.HeaderCell color='grey' textAlign='center' width={2}>Storage</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {queueElements}
                </Table.Body>
              </Table>
          }

          <Button primary onClick={() => setIsModalOpen(true)}>
            <Icon name='plus' />
            Add investor to queue
          </Button>

        </Collapsible.Content>
      </Collapsible.Root>
      {
        isModalOpen &&
        <AddEditInvestorModal
          closeModal={() => setIsModalOpen(false)}
        />
      }
    </div >
  );
};

export default InvestorQueueTable;
